import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, Typography, Avatar, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';



const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
  padding: theme.spacing(3),
  background: '#f5f6fa', // subtle light gray
}));

const ChatBubble = styled(Paper)(({ theme, owner }) => ({
  display: 'flex',
  alignItems: 'center',
  background: owner ? '#ececec' : '#fff', // user: more grey, assistant: white
  color: '#222',
  borderRadius: 16,
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  maxWidth: '75%',
  padding: theme.spacing(2, 3),
}));

const ChatAvatar = styled(Avatar)(() => ({
  margin: '0 12px',
  background: '#e3e7ed', // subtle gray
  color: '#b0b7c3', // medium grey for icon
  width: 36,
  height: 36,
}));

export default function CustomerDetail() {
  const { user_id } = useParams();
  const [user, setUser] = useState({ name: '', report: '', conversation: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchUser() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`https://api.uxhq.com/user_report?user_id=${user_id}`);
        if (!res.ok) throw new Error('User not found');
        const data = await res.json();
        // Clean up markdown backticks if present
        let report = data.report;
        if (typeof report === 'string') {
          // Remove only the first ```markdown or ``` and the last ```
          report = report.replace(/^```markdown\s*\n?/, '').replace(/^```\s*\n?/, '').replace(/```$/, '').trim();
        }
        // Ensure conversation is an array
        let conversation = data.conversation;
        if (typeof conversation === 'string') {
          try { conversation = JSON.parse(conversation); } catch { conversation = []; }
        }
        if (!Array.isArray(conversation)) conversation = [];
        setUser({ name: data.name, report, conversation });
      } catch (err) {
        setError('Could not load user details.');
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [user_id]);

  if (loading) return <Box sx={{ p: 4 }}>Loading...</Box>;
  if (error) return <Box sx={{ p: 4, color: 'red' }}>{error}</Box>;

  return (
    <Box sx={{ flexGrow: 1, width: '100%', p: { xs: 1, sm: 2, md: 4 }, mt: 10, background: '#f8f9fb', minHeight: '100vh' }}>
      {/* Floating summary box */}
      <Card sx={{ width: '100%', maxWidth: 600, mx: 'auto', mb: 4, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)', background: '#fff', p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Avatar sx={{ width: 56, height: 56, background: '#e3e7ed', color: '#b0b7c3' }}>
          <PersonIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700}>{user.name}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
      </Card>
      {/* Side-by-side chat and report */}
      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 220px)' }}>
        {/* Chat column */}
        <Box sx={{ flex: 1, height: '100%', background: '#fff', borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <ChatContainer sx={{ flex: 1, height: '100%', overflowY: 'auto', p: 3 }}>
            {Array.isArray(user.conversation) && user.conversation.length > 0 ? user.conversation.map((msg, idx) => (
              <Box key={idx} sx={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
                <ChatAvatar>
                  {msg.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                </ChatAvatar>
                <ChatBubble owner={msg.role === 'user'}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {msg.content}
                  </Typography>
                </ChatBubble>
              </Box>
            )) : <Typography color="#b0b7c3">No conversation found.</Typography>}
          </ChatContainer>
        </Box>
        {/* Report column */}
        <Box sx={{ flex: 1, height: '100%', background: '#fff', borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, height: '100%', overflowY: 'auto', p: 3 }}>
            <ReactMarkdown>{user.report || 'No report found.'}</ReactMarkdown>
          </Box>
        </Box>
      </Box>
    </Box>
  );
} 